import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Modal } from "reactstrap";
import DataTablePageQuery from "../../../components/DataTablePageQuery";
import { fetchTags } from "../../tags/data/tagService";
import { fetchQuoteTemplateByFilter, deleteQuoteTemplate } from "../data/quoteTemplateService";
import AdminQuoteTemplateModal from "./AdminQuoteTemplateModal";
import { formatDateTime } from "../../../utils/dateUtils";
import { createAudit } from '../../../features/audit/data/auditService';
import TagsList from "../../tags/components/TagsList";
import { useLockState } from "../../../components/userLocks/LockCheck";

const AdminQuoteTemplateTable = ({ mode = "admin", selectTemplate }) => {
    // general state
    const [modal, setModal] = useState(false);
    const [downloadButtonIsEnabled, setDownloadButtonIsEnabled] = useState(true);
    const searchableFields = ["name", "description", "tags", "createdAt", "updatedAt", "disabled"]
    const lockState = useLockState();

    // template state
    const [quoteTemplates, setQuoteTemplates] = useState([]);
    const [selectedQuoteTemplate, setSelectedQuoteTemplate] = useState(null);

    // pagination state
    const [filter, setFilter] = useState({ pageNumber: 1, pageSize: 10 });
    const [totalRecords, setTotalRecords] = useState(0);

    // Fetch Tags using useQuery
    const {
        data: tagsData,
        isLoading: isLoadingTags,
        error: tagsError,
    } = useQuery({
        queryKey: ["tagsFilter"],
        queryFn: fetchTags,
        staleTime: 60000
    });

    const getQuoteTemplates = async () => {
        const quoteTemplates = await fetchQuoteTemplateByFilter(filter);
        setTotalRecords(quoteTemplates.totalRecords);
        if (quoteTemplates.totalRecords === 0) return []

        return quoteTemplates.quoteTemplateItems.map(({ id, name, description, quoteTemplateTags, s3Key, disabled, createdAt, updatedAt, customSections, customSectionsConfig }) => {
            return {
                id,
                name,
                description,
                tags: quoteTemplateTags.length > 0
                    ? quoteTemplateTags
                        .map(tag => {
                            const tagData = tagsData.find(w => w.id === tag.tagId);
                            return tagData
                                ? {
                                    id: tag.tagId,
                                    tagName: tagData.tagName,
                                    colour: tagData.colour
                                }
                                : null;
                        })
                        .filter(Boolean) // removes null entries
                    : [],
                s3Key,
                disabled,
                createdAt,
                updatedAt,
                customSections,
                customSectionsConfig
            };
        });
    }

    // Fetch data using useQuery
    const {
        data: quoteTemplateData,
        isLoading: isLoadingQuoteTemplateData,
        error: quoteTemplateDataError,
        refetch: refetchQuoteTemplates,
    } = useQuery({
        queryKey: ["quoteTemplates"],
        queryFn: getQuoteTemplates,
        enabled: !!tagsData,
        staleTime: 60000
    });

    const tagsOptions = tagsData || [];

    // this query will run when the user presses the 'download' button
    // it will be passed as a prop to the Download component and will fetch all records that meet the filter criteria, not just the current page records
    const getQuoteTemplateQueryForDownload = async () => {
        let downloadFilter = { ...filter }
        delete downloadFilter.pageSize
        delete downloadFilter.pageNumber

        setDownloadButtonIsEnabled(false);

        const quoteTemplates = await fetchQuoteTemplateByFilter(downloadFilter);

        setDownloadButtonIsEnabled(true);

        return quoteTemplates.quoteTemplateItems.map(({ id, name, description, quoteTemplateTags, s3Key, disabled, createdAt, updatedAt }) => {
            return {
                id,
                name,
                description,
                tags: quoteTemplateTags.length > 0
                    ? quoteTemplateTags
                        .map(t => tagsData.find(w => w.id === t.tagId)?.tagName)
                        .filter(Boolean)
                        .join(", ")
                    : "",
                s3Key,
                disabled,
                createdAt: createdAt ? formatDateTime(createdAt) : null,
                updatedAt: updatedAt ? formatDateTime(updatedAt) : null
            };
        });
    }

    // When data is fetched, update our local state.
    useEffect(() => {
        if (quoteTemplateData) {
            setQuoteTemplates(quoteTemplateData);
        }
    }, [quoteTemplateData]);

    useEffect(() => {
        if (tagsData) {
            refetchQuoteTemplates();
        }
    }, [filter, tagsData]);

    const changeFilter = (obj) => {
        const filterId = obj.filterId
        const value = obj.value

        if (filterId === "pageNumber") {
            setFilter(prev => ({ ...prev, [filterId]: value }));
        } else if (filterId === "tags") {
            if (value === undefined) {
                setFilter(({ quoteTemplateTagId, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                setFilter(prev => ({ ...prev, quoteTemplateTagId: value, pageNumber: 1 }));
            }
        } else if (filterId === "searchTerm") {
            if (value === '') {
                setFilter(({ searchOptions, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                let searchOptions = { searchableFields: searchableFields, searchTerm: value, fieldSearchTerms: [] }

                // try to find tags in search parameter
                // if found, add the tagIds to the searchOptions
                if (searchableFields.find(w => w === "tags") && tagsData) {
                    const tagIds = tagsData.filter(w => w.tagName.toLowerCase().includes(value.toLowerCase())).map(u => u.id.toString());
                    if (tagIds.length > 0) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "tags", "values": tagIds })
                    }
                }

                // try to find the words 'true' and 'false' in search parameter
                // if found, add 'disabled' to the searchOptions
                if (searchableFields.find(w => w === "disabled")) {
                    const disabledPossibleValues = ["y", "yes"];
                    const enabledPossibleValues = ["n", "no"];
                    if (disabledPossibleValues.includes(value.trim().toLowerCase())) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "disabled", "values": ["true"] })
                    } else if (enabledPossibleValues.includes(value.trim().toLowerCase())) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "disabled", "values": ["false"] })
                    }
                }

                setFilter(prev => ({ ...prev, searchOptions: searchOptions, pageNumber: 1 }));
            }
        } else {
            setFilter(prev => ({ ...prev, [filterId]: value, pageNumber: 1 }));
        }
    }

    // Handle row click to open modal with details
    const handleRowClick = (item) => {
        if (mode === "user") return;
        setSelectedQuoteTemplate(item);
        setModal(true);         // Open modal
    };

    const onToggle = () => {
        setQuoteTemplates((prevQuoteTemplates) => prevQuoteTemplates.filter(i => i.id !== 0))
        setModal(!modal)
    }

    const handleTemplateChangeFromModal = (template, isNewTemplate) => {
        setSelectedQuoteTemplate(template)

        if (isNewTemplate) {
            setQuoteTemplates([...quoteTemplates, template]);
        } else {
            setQuoteTemplates(quoteTemplates.map((i) => i.id === template.id ? template : i));
        }
    }

    const handleNewTemplateClick = () => {
        const newTemplate = {
            "id": 0, "name": null, "description": null, "tags": [], "file": null, "disabled": false
        }
        setQuoteTemplates([...quoteTemplates, newTemplate]); // Add new template to quoteTemplates array (newTemplate)
        setSelectedQuoteTemplate(newTemplate);
        setModal(true);
    }

    const columns = [
        {
            key: "name",
            label: "Name",
        },
        {
            key: "description",
            label: "Description",
        },
        {
            key: "createdAt",
            label: "Created",
            render: (value) => (
                <span>{value ? formatDateTime(new Date(value)) : ""}</span>
            )
        },
        {
            key: "updatedAt",
            label: "Updated",
            render: (value) => (
                <span>{value ? formatDateTime(new Date(value)) : ""}</span>
            )
        },
        {
            key: "tags",
            label: "Tags",
            render: (value, row) => (
                <TagsList tags={row.tags} />
            )
        },
        ...mode === "admin" ? [
            {
                key: "disabled",
                label: "Disabled",
                render: (value) => (
                    <span>{value ? "Yes" : "No"}</span>
                )
            },
        ] : [],
        ...mode === "user" ? [
            {
                key: "actions",
                label: "",
                className: "text-end",
                render: (value, row) => (
                    <Button className={lockState?.isLocked ? "locked" : ""} onClick={() => selectTemplate(row)}>Select</Button>
                )
            },
        ] : [],
    ];

    const handleDelete = async () => {
        setQuoteTemplates((prevItems) => prevItems.filter(f => f.id !== selectedQuoteTemplate.id));
        await deleteQuoteTemplate(selectedQuoteTemplate.id);

        await createAudit({ "action": "Quote template deleted", "result": "Successful", "source": `quoteTemplate.${selectedQuoteTemplate.id}`, "payload": `{\"name\": \"${selectedQuoteTemplate.name}\" }` })

        setSelectedQuoteTemplate(null);
        setModal(false);
    }

    return (
        <>
            <DataTablePageQuery
                columns={columns}
                data={quoteTemplates}
                queryForDownload={getQuoteTemplateQueryForDownload}
                downloadButtonIsEnabled={downloadButtonIsEnabled}
                csvFileName="quoteTemplates.csv"
                exportToCsv={false}
                totalRecords={totalRecords}
                sortConfiguration={{ key: "name", direction: "ascending" }}
                onRowClick={(row) => handleRowClick(row)}
                onFilterChange={changeFilter}
                error={quoteTemplateDataError || tagsError}
                isLoading={isLoadingQuoteTemplateData || isLoadingTags}
                filters={[
                    {
                        id: "tags",
                        label: "Tag",
                        type: "select",
                        isSearchable: true,
                        options: tagsOptions.map((item) => ({
                            value: item.id,
                            label: item.tagName,
                        }))
                    }
                ]}
                actions={
                    [
                        mode === "admin" && <Button onClick={handleNewTemplateClick}>Add New Template</Button>
                    ]
                }

            />
            {/* Modal for displaying selected template details */}
            <Modal isOpen={modal} centered size="xl" fullscreen scrollable backdrop="static"
            >
                {selectedQuoteTemplate &&
                    <AdminQuoteTemplateModal
                        toggle={onToggle}
                        template={selectedQuoteTemplate}
                        tagsData={tagsData || []}
                        onTemplateChange={(updatedTemplate, isNewTemplate) => handleTemplateChangeFromModal(updatedTemplate, isNewTemplate)}
                        handleDelete={handleDelete}
                    />
                }
            </Modal>
        </>
    );
}
export default AdminQuoteTemplateTable;